import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useSettings } from '../context/SettingsContext';
import Card from '../components/Card';
import Button from '../components/Button';
import FormInput from '../components/FormInput';
import Alert from '../components/Alert';
import { useZone } from '../context/ZoneContext';

const SettingsContainer = styled.div`
  padding: 1.5rem;
`;

const PageTitle = styled.h1`
  margin: 0 0 1.5rem 0;
  font-size: ${({ theme }) => theme.typography.sizes.xl};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const SettingsSection = styled.div`
  margin-bottom: 2rem;
`;

// SectionTitle styled component removed as it was not being used
const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const SettingsPage = () => {
  const { state: settingsState, updateSettings } = useSettings();
  const { settings } = settingsState;
  const { state: { zones } } = useZone();
  
  // Notification state
  const [notification, setNotification] = useState(null);
  
  const [formValues, setFormValues] = useState({
    theme: settings?.theme || 'light',
    temperatureUnit: settings?.temperatureUnit || 'C',
    notificationsEnabled: settings?.notificationsEnabled !== false,
    autoRefreshInterval: settings?.autoRefreshInterval || 5,
    defaultZoneId: settings?.defaultZoneId || '',
    language: settings?.language || 'en'
  });
  
  const [isFormDirty, setIsFormDirty] = useState(false);
  
  // Update form values when settings change
  useEffect(() => {
    setFormValues({
      theme: settings?.theme || 'light',
      temperatureUnit: settings?.temperatureUnit || 'C',
      notificationsEnabled: settings?.notificationsEnabled !== false,
      autoRefreshInterval: settings?.autoRefreshInterval || 5,
      defaultZoneId: settings?.defaultZoneId || '',
      language: settings?.language || 'en'
    });
  }, [settings]);
  
  // Handle input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormValues(prev => ({
      ...prev,
      [name]: newValue
    }));
    
    setIsFormDirty(true);
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Update settings in context
    updateSettings(formValues);
    setIsFormDirty(false);
    
    // Show success notification
    setNotification({
      type: 'success',
      message: 'Settings saved successfully!'
    });
  };
  
  // Reset form to saved settings
  const handleReset = () => {
    setFormValues({
      theme: settings?.theme || 'light',
      temperatureUnit: settings?.temperatureUnit || 'C',
      notificationsEnabled: settings?.notificationsEnabled !== false,
      autoRefreshInterval: settings?.autoRefreshInterval || 5,
      defaultZoneId: settings?.defaultZoneId || '',
      language: settings?.language || 'en'
    });
    
    setIsFormDirty(false);
  };

  return (
    <SettingsContainer>
      <PageTitle>Settings</PageTitle>
      
      {notification && (
        <Alert 
          type={notification.type} 
          message={notification.message} 
          dismissible
          onClose={() => setNotification(null)}
        />
      )}
      
      <form onSubmit={handleSubmit}>
        <Card title="Display Settings">
          <SettingsSection>
            <FormGroup>
              <FormInput
                type="select"
                label="Theme"
                name="theme"
                value={formValues.theme}
                onChange={handleInputChange}
                options={[
                  { value: 'light', label: 'Light' },
                  { value: 'dark', label: 'Dark' },
                  { value: 'system', label: 'System Default' }
                ]}
              />
            </FormGroup>
            
            <FormGroup>
              <FormInput
                type="select"
                label="Temperature Unit"
                name="temperatureUnit"
                value={formValues.temperatureUnit}
                onChange={handleInputChange}
                options={[
                  { value: 'C', label: 'Celsius (°C)' },
                  { value: 'F', label: 'Fahrenheit (°F)' }
                ]}
              />
            </FormGroup>
            
            <FormGroup>
              <FormInput
                type="select"
                label="Language"
                name="language"
                value={formValues.language}
                onChange={handleInputChange}
                options={[
                  { value: 'en', label: 'English' },
                  { value: 'es', label: 'Spanish' },
                  { value: 'fr', label: 'French' }
                ]}
              />
            </FormGroup>
          </SettingsSection>
        </Card>
        
        <Card title="Notification Settings" style={{ marginTop: '1.5rem' }}>
          <SettingsSection>
            <FormGroup>
              <FormInput
                type="checkbox"
                label="Enable Notifications"
                name="notificationsEnabled"
                checked={formValues.notificationsEnabled}
                onChange={handleInputChange}
              />
            </FormGroup>
          </SettingsSection>
        </Card>
        
        <Card title="Data Settings" style={{ marginTop: '1.5rem' }}>
          <SettingsSection>
            <FormGroup>
              <FormInput
                type="select"
                label="Auto-refresh Interval"
                name="autoRefreshInterval"
                value={formValues.autoRefreshInterval}
                onChange={handleInputChange}
                options={[
                  { value: 1, label: '1 minute' },
                  { value: 5, label: '5 minutes' },
                  { value: 15, label: '15 minutes' },
                  { value: 30, label: '30 minutes' },
                  { value: 60, label: '1 hour' }
                ]}
              />
            </FormGroup>
            
            <FormGroup>
              <FormInput
                type="select"
                label="Default Zone"
                name="defaultZoneId"
                value={formValues.defaultZoneId}
                onChange={handleInputChange}
                options={[
                  { value: '', label: 'No default zone' },
                  ...(zones?.map(zone => ({ value: zone.id, label: zone.name })) || [])
                ]}
              />
            </FormGroup>
          </SettingsSection>
        </Card>
        
        <ButtonGroup>
          <Button 
            type="submit" 
            variant="primary" 
            disabled={!isFormDirty}
          >
            Save Settings
          </Button>
          <Button 
            type="button" 
            variant="outline" 
            onClick={handleReset}
            disabled={!isFormDirty}
          >
            Reset Changes
          </Button>
        </ButtonGroup>
      </form>
    </SettingsContainer>
  );
};

export default SettingsPage;
